import React, { useRef } from 'react';
import { Modal } from '@/components/ui';
import { Download } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { API_BASE_URL } from '@/services/api';

export const ViewOrderItemsModal = ({ isOpen, onClose, estimation }) => {
  const contentRef = useRef(null);
  if (!estimation) return null;

  const items = estimation.items || [];
  const customerName = estimation.customer_id?.name || estimation.customer_id?.customerName || estimation.customerName || 'Customer';

  const handleDownload = () => {
    const element = contentRef.current;
    if (!element) return;
    
    const opt = {
      margin:       0.5,
      filename:     `Order_Items_${customerName.replace(/\s+/g, '_')}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2 },
      jsPDF:        { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    html2pdf().set(opt).from(element).save();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Order Items - ${customerName}`} size="max-w-4xl">
      <div className="space-y-4">
        {items.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No items found for this order.</div>
        ) : (
          <div ref={contentRef} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <div className="px-4 py-3 bg-white border-b border-slate-200 html2pdf__header hidden">
              <h2 className="text-lg font-bold text-slate-800">Order Items - {customerName}</h2>
            </div>
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200 text-slate-600">
                  <th className="px-4 py-3 font-medium">S.No</th>
                  <th className="px-4 py-3 font-medium w-20">Image</th>
                  <th className="px-4 py-3 font-medium">Product</th>
                  <th className="px-4 py-3 font-medium">Thickness</th>
                  <th className="px-4 py-3 font-medium">Quality</th>
                  <th className="px-4 py-3 font-medium text-center">Quantity</th>
                  <th className="px-4 py-3 font-medium">Fix / Loose</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {items.map((item, idx) => {
                  const productTitle = item.item_details?.product || item.item_details?.name || item.item_details?.title || item.name || 'Custom Item';
                  const w = item.width || item.item_details?.width;
                  const h = item.height || item.item_details?.height;
                  const displaySize = item.dimensions || item.item_details?.dimensions || (w && h ? `${w} x ${h}` : '');
                  const productDisplay = displaySize ? `${productTitle} (${displaySize})` : productTitle;
                  const fixingType = item.item_details?.fixingType || 'Loose';
                  const qty = Number(item.qty) || Number(item.quantity) || 1;

                  const imageUrl = item.image_id ? `${API_BASE_URL}/api/estimations/images/${item.image_id}` : (item.item_details?.image ? 
                    (item.item_details.image.startsWith('http') ? item.item_details.image : (item.item_details.image.startsWith('/') ? 
                    `${API_BASE_URL}${item.item_details.image}` : 
                    `${API_BASE_URL}/api/inventory/settings/images/${item.item_details.image}`)) : '');

                  return (
                    <tr key={idx} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                      <td className="px-4 py-3">
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt="Item" 
                            className="w-12 h-12 object-cover rounded-md border border-slate-200 bg-white"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-slate-100 rounded-md border border-slate-200 flex items-center justify-center text-slate-400 text-xs">
                            No Img
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800">{productDisplay}</td>
                      <td className="px-4 py-3 text-slate-600">{item.item_details?.thickness || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{item.item_details?.quality || 'Premium'}</td>
                      <td className="px-4 py-3 text-slate-800 font-semibold text-center">{qty}</td>
                      <td className="px-4 py-3">
                        {fixingType === 'Ready to Fix' ? (
                          <span className="px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-full bg-indigo-100 text-indigo-700">
                            {fixingType}
                          </span>
                        ) : (
                          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                            {fixingType}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-5 py-2 bg-emerald-50 text-emerald-600 rounded-lg text-sm font-semibold hover:bg-emerald-100 transition-colors"
          >
            <Download size={16} strokeWidth={2.5} />
            Download PDF
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};
